import { Link } from "react-router-dom";
import { Bell, BarChart3, Package, FileText, Briefcase, Calendar, Mail, BadgePercent, Newspaper, Globe, BookOpen, Radio, ChevronLeft, ChevronRight, Home, Receipt } from "lucide-react";
import { Badge } from "./ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

export const SIDEBAR_SECTIONS = [
  {
    label: "Overview",
    items: [
      { id: "analytics", label: "Analytics", icon: BarChart3 },
      { id: "notifications", label: "Notifications", icon: Bell, badgeKey: "unread_notifications" },
    ],
  },
  {
    label: "Customers",
    items: [
      { id: "orders", label: "Orders & Payments", icon: Package, badgeKey: "action_items" },
      { id: "inquiries", label: "Inquiries", icon: FileText, badgeKey: "inquiries" },
      { id: "service-requests", label: "Service Requests", icon: Briefcase },
      { id: "proformas", label: "Pro Formas", icon: Receipt },
    ],
  },
  {
    label: "Operations",
    items: [
      { id: "schedules", label: "Schedules", icon: Calendar },
      { id: "reminders", label: "Reminders", icon: Mail },
      { id: "promo-codes", label: "Promo Codes", icon: BadgePercent },
    ],
  },
  {
    label: "Content",
    items: [
      { id: "courses", label: "Courses", icon: BookOpen },
      { id: "blog", label: "Blog", icon: Newspaper },
      { id: "resources", label: "Resources", icon: Globe },
      { id: "scraper", label: "Media Scraper", icon: Radio },
    ],
  },
];

const AdminSidebar = ({ activeSection, onSectionChange, badges = {}, collapsed, onToggleCollapsed }) => {
  return (
    <TooltipProvider delayDuration={200}>
      <aside
        className={`sticky top-0 h-screen bg-white border-r border-slate-200 flex flex-col transition-all duration-200 ${
          collapsed ? "w-16" : "w-60"
        }`}
        data-testid="admin-sidebar"
      >
        {/* Brand */}
        <div className="h-16 flex items-center px-4 border-b border-slate-200 flex-shrink-0">
          <Link to="/" className="flex items-center gap-2 overflow-hidden">
            <div className="w-8 h-8 bg-brand text-white rounded-md flex items-center justify-center flex-shrink-0 font-bold text-sm">
              CM
            </div>
            {!collapsed && <span className="font-heading font-bold text-primary text-sm whitespace-nowrap">Comply CMS</span>}
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3">
          {SIDEBAR_SECTIONS.map((section) => (
            <div key={section.label} className="mb-4">
              {!collapsed && (
                <p className="px-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {section.label}
                </p>
              )}
              <ul>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = activeSection === item.id;
                  const badge = item.badgeKey ? badges[item.badgeKey] : null;
                  const btn = (
                    <button
                      type="button"
                      onClick={() => onSectionChange(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                        active
                          ? "bg-brand/10 text-brand font-medium border-r-2 border-brand"
                          : "text-slate-700 hover:bg-slate-50"
                      } ${collapsed ? "justify-center px-2" : ""}`}
                      data-testid={`nav-${item.id}`}
                    >
                      <Icon className={`h-4 w-4 flex-shrink-0 ${active ? "text-brand" : "text-slate-500"}`} />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                      {!collapsed && badge > 0 && (
                        <Badge className="ml-auto bg-orange-500 text-white h-5 min-w-[20px] px-1.5 text-[10px]">
                          {badge}
                        </Badge>
                      )}
                      {collapsed && badge > 0 && (
                        <span className="absolute right-1 top-1 w-2 h-2 bg-orange-500 rounded-full" />
                      )}
                    </button>
                  );
                  return (
                    <li key={item.id} className="relative">
                      {collapsed ? (
                        <Tooltip>
                          <TooltipTrigger asChild>{btn}</TooltipTrigger>
                          <TooltipContent side="right" sideOffset={8}>
                            {item.label}
                            {badge > 0 && ` (${badge})`}
                          </TooltipContent>
                        </Tooltip>
                      ) : btn}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer: back to site + collapse */}
        <div className="border-t border-slate-200 p-2 flex-shrink-0 space-y-1">
          <Link
            to="/"
            className={`flex items-center gap-3 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md ${collapsed ? "justify-center" : ""}`}
          >
            <Home className="h-4 w-4" />
            {!collapsed && <span>Back to site</span>}
          </Link>
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="w-full flex items-center justify-center py-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-md transition-colors"
            data-testid="toggle-sidebar"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
      </aside>
    </TooltipProvider>
  );
};

export default AdminSidebar;
